export interface SystemRole {
  id: number;
  code: string;
  name: string;
  description: string;
  displayOrder: number;
  active: boolean;
}

export interface CompanyRoleModulePermission {
  id: number;
  companyId: number;
  systemRoleId: number;
  systemRoleCode: string;
  systemRoleName: string;
  moduleCode: string;
  permissions: number;
  active: boolean;
  canRead: boolean;
  canCreate: boolean;
  canUpdate: boolean;
  canDelete: boolean;
  canExport: boolean;
  canImport: boolean;
}

export interface PermissionRequest {
  companyId: number;
  systemRoleId: number;
  moduleCode: string;
  permissions: number;
  active: boolean;
}

export enum PermissionFlag {
  READ = 1, // 2^0 = 1
  CREATE = 2, // 2^1 = 2
  UPDATE = 4, // 2^2 = 4
  DELETE = 8, // 2^3 = 8
  EXPORT = 16, // 2^4 = 16
  IMPORT = 32, // 2^5 = 32
  ALL = 63, // 1+2+4+8+16+32 = 63
}

export const PermissionFlagLabels: Record<PermissionFlag, string> = {
  [PermissionFlag.READ]: 'Leer',
  [PermissionFlag.CREATE]: 'Crear',
  [PermissionFlag.UPDATE]: 'Actualizar',
  [PermissionFlag.DELETE]: 'Eliminar',
  [PermissionFlag.EXPORT]: 'Exportar',
  [PermissionFlag.IMPORT]: 'Importar',
  [PermissionFlag.ALL]: 'Todos',
};

// Helper: Verificar si ACCOUNTING debe estar visible según ambiente
const shouldShowAccounting = (): boolean => {
  const env = import.meta.env.VITE_ENVIRONMENT;
  // Ocultar ACCOUNTING solo en PRODUCCIÓN
  return env !== 'PROD';
};

// Módulos base del sistema
const allModules: Record<string, string> = {
  GLOBAL: 'moduleNames.GLOBAL',
  SALES: 'moduleNames.SALES',
  PURCHASES: 'moduleNames.PURCHASES',
  INVENTORY: 'moduleNames.INVENTORY',
  CONFIGURATION: 'moduleNames.CONFIGURATION',
  BUSINESS_MODULE: 'moduleNames.BUSINESS_MODULE',
  USERS: 'moduleNames.USERS',
  ACCOUNTING: 'moduleNames.ACCOUNTING',
};

// Filtrar ACCOUNTING según ambiente (oculto en PROD, visible en DEV/CERT/LOCAL)
export const ModuleLabels: Record<string, string> = Object.fromEntries(
  Object.entries(allModules).filter(([code]) => {
    if (code === 'ACCOUNTING') {
      return shouldShowAccounting();
    }
    return true;
  }),
);

export class PermissionHelper {
  static hasPermission(permissions: number, flag: PermissionFlag): boolean {
    return (permissions & flag) !== 0;
  }

  static addPermission(permissions: number, flag: PermissionFlag): number {
    return permissions | flag;
  }

  static removePermission(permissions: number, flag: PermissionFlag): number {
    return permissions & ~flag;
  }

  static togglePermission(permissions: number, flag: PermissionFlag): number {
    return permissions ^ flag;
  }

  static getPermissionFlags(permissions: number): PermissionFlag[] {
    const flags: PermissionFlag[] = [];
    if (this.hasPermission(permissions, PermissionFlag.READ)) flags.push(PermissionFlag.READ);
    if (this.hasPermission(permissions, PermissionFlag.CREATE)) flags.push(PermissionFlag.CREATE);
    if (this.hasPermission(permissions, PermissionFlag.UPDATE)) flags.push(PermissionFlag.UPDATE);
    if (this.hasPermission(permissions, PermissionFlag.DELETE)) flags.push(PermissionFlag.DELETE);
    if (this.hasPermission(permissions, PermissionFlag.EXPORT)) flags.push(PermissionFlag.EXPORT);
    if (this.hasPermission(permissions, PermissionFlag.IMPORT)) flags.push(PermissionFlag.IMPORT);
    return flags;
  }
}
